// public/v3/src/lib/program-card-model.js
// Pure view-model for one Studio program card (CCM or BHI). CommonJS so Jest
// can require() it (repo has no ESM transform); esbuild bundles CJS fine when
// imported from JSX. No React, no network — maps already-computed workflow
// inputs to a flat descriptor the presentational <ProgramCard> renders verbatim.
'use strict';

function formatAssessmentText(a) {
  if (!a) return null;
  const score = a.score != null ? ` ${a.score}` : '';
  const sev = a.severity ? ` (${a.severity})` : '';
  const date = a.date ? ` · ${a.date}` : '';
  return `Latest: ${a.scale}${score}${sev}${date}`;
}

// input: {
//   program:'ccm'|'bhi', code, label, target, logged, isApcm, folded, eligible,
//   eligReason, workflow:{needsGenerate,needsPushSign,hasBillingNote,isBilled},
//   gates:{ok,blockers}, assessment:{scale,score,severity,date}|null,
//   busy:{gen,push}
// }
// workflow is the full object from getWorkflowState (needsGenerate, needsPushSign,
// hasBillingNote, isBilled). For CCM, the met/short variant is derived from minutes
// (logged vs target), NOT from needsGenerate.
function programCardModel(input) {
  const i = input || {};
  const program = i.program === 'bhi' ? 'bhi' : 'ccm';
  const workflow = i.workflow || {};
  const gates = i.gates || { ok: true, blockers: [] };
  const busy = i.busy || {};
  const eligible = !!i.eligible;
  const target = Number(i.target) || 0;
  const logged = Number(i.logged) || 0;

  // The missing-assessment blocker is a SOFT guard: when it is the ONLY reason
  // the gates aren't OK, the operator can still explicitly proceed (warned +
  // audited at generate/push time). So it must NOT lock the card the way the
  // hard blockers (enrollment, consent, minutes) do — the card stays 'normal'
  // with its actions present and the '⚠ No initial assessment' warning shown.
  const ASSESSMENT_BLOCKER = 'Initial assessment missing';
  const blockerList = gates.blockers || [];
  const onlyAssessmentBlocks = !gates.ok
    && blockerList.length > 0
    && blockerList.every((b) => b === ASSESSMENT_BLOCKER);

  let variant;
  if (program === 'bhi') {
    if (i.folded) variant = gates.ok ? 'bhi-folded' : 'bhi-folded-blocked';
    else if (!gates.ok && !onlyAssessmentBlocks) variant = 'bhi-gated';
    else variant = 'bhi-normal';
  } else if (i.isApcm) {
    variant = 'apcm';
  } else {
    // Caller sets isApcm whenever the program has no minute target, so a non-APCM
    // CCM card always has target > 0 by contract — logged >= target cannot misfire on 0.
    variant = logged >= target ? 'met' : 'short';
  }

  // CCM minute-states reuse the roster StatusPill (visual tie to the roster).
  // BHI states use a tinted chip. statusChip is ALWAYS set so the compact iPad
  // band has something to render regardless of program.
  let statusPill = null;            // 'ready'|'short'|'apcm' for CCM, else null
  let statusChip = null;            // { label, tone:'ok'|'warn' }
  if (variant === 'met')                     { statusPill = 'ready'; statusChip = { label: 'Met', tone: 'ok' }; }
  else if (variant === 'short')              { statusPill = 'short'; statusChip = { label: 'Short', tone: 'warn' }; }
  else if (variant === 'apcm')               { statusPill = 'apcm';  statusChip = { label: 'flat fee', tone: 'ok' }; }
  else if (variant === 'bhi-folded')         { statusChip = { label: 'folded', tone: 'ok' }; }
  else if (variant === 'bhi-folded-blocked') { statusChip = { label: 'not ready', tone: 'warn' }; }
  else if (variant === 'bhi-gated')          { statusChip = { label: 'not ready', tone: 'warn' }; }
  else if (variant === 'bhi-normal') {
    const label = i.assessment
      ? `${i.assessment.scale}${i.assessment.score != null ? ` ${i.assessment.score}` : ''}`
      : 'no assessment';
    statusChip = { label, tone: 'warn' };
  }

  const showProgress = variant === 'met' || variant === 'short' || variant === 'bhi-normal';
  const timeText = variant === 'apcm'
    ? 'no minute target'
    : (showProgress ? `${logged} / ${target} min` : '');

  let assessmentText = null;
  let showHistory = false;
  if (variant === 'bhi-normal') {
    if (i.assessment) { assessmentText = formatAssessmentText(i.assessment); showHistory = true; }
    else { assessmentText = '⚠ No initial assessment'; }
  }

  const actionsLocked = variant === 'bhi-gated' || variant === 'bhi-folded' || variant === 'bhi-folded-blocked';
  const primaryAction = actionsLocked ? null : {
    label: workflow.hasBillingNote ? 'Regenerate' : 'Generate',
    kind: workflow.hasBillingNote ? 'regenerate' : 'generate',
    disabled: !!busy.gen || !eligible,
    busy: !!busy.gen,
    title: !eligible
      ? (i.eligReason || '')
      : workflow.hasBillingNote
        ? (workflow.isBilled
            ? 'Regenerate plan & billing note (clears submitted status — requires re-Push & Sign)'
            : 'Regenerate this month’s plan & billing note')
        : 'Generate this month’s care plan and billing note',
  };
  const secondaryAction = actionsLocked ? null : {
    label: 'Push & Sign',
    disabled: !!busy.push || !workflow.needsPushSign,
    busy: !!busy.push,
    title: workflow.isBilled
      ? 'Already submitted this cycle. Regenerate to re-push.'
      : (workflow.needsPushSign ? 'Push to Elation and sign' : 'Generate first'),
  };

  let redirect = null;
  if (variant === 'bhi-folded') redirect = { label: 'Open CCM view →', target: 'ccm' };
  else if (variant === 'bhi-folded-blocked') redirect = { label: 'Open in Time Logs →', target: 'timelogs' };
  else if (variant === 'bhi-gated') redirect = { label: 'Open in Time Logs →', target: 'timelogs' };

  return {
    program,
    code: i.code || '',
    label: i.label || i.code || '',
    variant,
    statusPill,
    statusChip,
    showProgress,
    progress: { value: logged, target },
    timeText,
    blockers: (variant === 'bhi-gated' || variant === 'bhi-folded-blocked') ? (gates.blockers || []) : [],
    assessmentText,
    showHistory,
    foldedHint: variant === 'bhi-folded'
      ? 'Bills on the APCM combined note, not a standalone 99484.'
      : variant === 'bhi-folded-blocked'
        ? 'G0570 won\'t attach until these clear — patient bills APCM only.'
        : '',
    primaryAction,
    secondaryAction,
    redirect,
    // Persistent message from a failed generate attempt for this card's
    // (patient, month). Rendered as a sticky warning so a transient AI failure
    // isn't lost when the toast auto-dismisses. Cleared on success/retry.
    genError: i.genError || null,
  };
}

module.exports = { programCardModel };
